import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Loader2, FolderKanban } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { TableToolbar } from "@/components/TableToolbar";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FieldError } from "@/components/FieldError";
import { fmtDate, partyLabel } from "@/lib/format";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";

export default function AllProjects() {
  const { isOrgAdmin } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: projectData }, { data: companyData }] = await Promise.all([
        api.get("/projects"), api.get("/masters/companies"),
      ]);
      setProjects(projectData);
      setCompanies(companyData);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";
  const statuses = [...new Set(projects.map((p) => p.status).filter(Boolean))];
  const q = search.trim().toLowerCase();
  const visible = projects.filter((p) =>
    (!statusFilter || p.status === statusFilter) &&
    (!q || `${p.code} ${p.name} ${companyName(p.company_id)}`.toLowerCase().includes(q)));

  return (
    <div className="space-y-6" data-testid="all-projects-page">
      <PageHeader
        title="All Projects"
        description="Every project you have access to. Click a row to open the project."
        action={
          <div className="flex items-center gap-2">
            {(isOrgAdmin || can("PROJECT_LIST", "create")) && <CreateProjectDialog companies={companies} onCreated={load} />}
          </div>
        }
      />
      <TableToolbar
        search={search}
        onSearch={setSearch}
        filters={[{ key: "status", placeholder: "All statuses", value: statusFilter, onChange: setStatusFilter, options: statuses.map((v) => ({ value: v, label: v.replace(/_/g, " ") })) }]}
        testId="all-projects-toolbar"
      />
      <div className="rounded-lg border border-border bg-card">
        {!loading && projects.length === 0 ? (
          <EmptyState title="No projects yet" description="Create your first project to get started." testId="all-projects-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Company</TableHead>
                <TableHead>Contract Value</TableHead><TableHead>Start</TableHead><TableHead>Target End</TableHead><TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((p) => (
                <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/projects/${p.id}`)} data-testid={`project-row-${p.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{p.code}</TableCell>
                  <TableCell className="font-medium text-foreground">{p.name}</TableCell>
                  <TableCell className="text-muted-foreground">{companyName(p.company_id)}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{p.contract_value ? `₹${Number(p.contract_value).toLocaleString("en-IN")}` : "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(p.start_date)}</TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(p.target_end_date)}</TableCell>
                  <TableCell><StatusPill value={p.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {!loading && projects.length > 0 && (
          <div className="border-t border-border px-3 py-2 text-xs text-muted-foreground" data-testid="all-projects-count">
            Showing {visible.length} of {projects.length} projects
          </div>
        )}
      </div>
    </div>
  );
}

function CreateProjectDialog({ companies, onCreated }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    code: "", name: "", company_id: "", client_id: "", contract_value: "",
    location: "", start_date: "", target_end_date: "", dc_capacity_mwp: "", wbs_template_id: "__default",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  useEffect(() => {
    if (open) api.get("/revenue/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, [open]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/wbs-templates").then(({ data }) => {
      setTemplates(data);
      const preferred = data.find((t) => t.name === "Buildings / Mall / Residential") || data.find((t) => /building/i.test(t.name)) || data[0];
      if (preferred) setForm((f) => (f.wbs_template_id === undefined || f.wbs_template_id === "__default" ? { ...f, wbs_template_id: preferred.id } : f));
    }).catch(() => {});
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      const payload = {
        ...form,
        company_id: form.company_id || null,
        client_id: form.client_id || null,
        contract_value: form.contract_value || null,
        start_date: form.start_date || null,
        target_end_date: form.target_end_date || null,
        dc_capacity_mwp: form.dc_capacity_mwp ? Number(form.dc_capacity_mwp) : null,
        wbs_template_id: form.wbs_template_id && form.wbs_template_id !== "__default" ? form.wbs_template_id : null,
      };
      const { data } = await api.post("/projects", payload);
      toast.success(payload.wbs_template_id
        ? `Project ${data.code} created with its WBS, weights and DPR rows ready`
        : `Project ${data.code} created — add its WBS via Setup Library`);
      setOpen(false);
      onCreated?.();
      navigate(`/projects/${data.id}`);
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-project-button"><Plus className="h-4 w-4" /> New Project</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FolderKanban className="h-4 w-4" /> New Project</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-project-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Project code</Label><Input placeholder="Automatic if blank" data-testid="project-code-input" value={form.code} onChange={set("code")} className="mt-1.5" /><FieldError error={errors.code} /></div>
            <div><Label>Project name</Label><Input required data-testid="project-name-input" value={form.name} onChange={set("name")} className="mt-1.5" /><FieldError error={errors.name} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="project-company-select" required={false} placeholder="Select company" />
            <div>
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="project-client-select" className="mt-1.5"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{partyLabel(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contract value (₹)</Label><Input type="number" step="0.01" data-testid="project-contract-value-input" value={form.contract_value} onChange={set("contract_value")} className="mt-1.5" /></div>
            <div><Label>Location</Label><Input data-testid="project-location-input" value={form.location} onChange={set("location")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div />
          </div>
          <div>
            <Label>WBS template <span className="font-normal text-muted-foreground">(builds Engineering, Procurement &amp; Construction automatically)</span></Label>
            <Select value={form.wbs_template_id || "none"} onValueChange={(v) => setForm({ ...form, wbs_template_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="project-template-select" className="mt-1.5"><SelectValue placeholder="Select template" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template — I'll build the WBS myself</SelectItem>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.wbs_template_id && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {!(templates.find((t) => t.id === form.wbs_template_id)?.name || "").includes("Solar") ? "Quantities are filled from your BOQ after the project is created." : (form.dc_capacity_mwp ? `Quantities per MWp will be scaled for ${form.dc_capacity_mwp} MWp.` : "Enter the DC capacity so quantities per MWp can be calculated.")}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start date</Label><Input type="date" data-testid="project-start-date-input" value={form.start_date} onChange={set("start_date")} className="mt-1.5" /></div>
            <div><Label>Target end date</Label><Input type="date" data-testid="project-end-date-input" value={form.target_end_date} onChange={set("target_end_date")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-project-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create Project</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
